import React, { useEffect } from "react";
import { CustomTextField } from "./SellAiStyledComponents";
import { Grid, Typography } from "@material-ui/core";

export const getCenterStoneCarats = (data: any): number => {
  const caratVal = 
    data?.carat ??
    data?.carats ??
    data?.centerStoneCarat ??
    data?.centreStoneCarat ??
    data?.centerStoneCarats ??
    data?.centreStoneCarats ??
    data?.centerCarat ??
    data?.centreCarat ??
    (typeof data?.centreStone === 'object' && data?.centreStone !== null ? (data?.centreStone?.carat ?? data?.centreStone?.carats) : null) ??
    (typeof data?.centerStone === 'object' && data?.centerStone !== null ? (data?.centerStone?.carat ?? data?.centerStone?.carats) : null);

  if (caratVal !== undefined && caratVal !== null) {
    const parsed = parseFloat(caratVal);
    if (!isNaN(parsed)) return parsed;
  }

  if (data?.centerStoneWeightInGrams) {
    return parseFloat(data.centerStoneWeightInGrams) / 0.2;
  }
  return 0;
};

export const getSideStoneCarats = (data: any): number => {
  if (Array.isArray(data?.sideStones)) {
    return data.sideStones.reduce((sum: number, s: any) => {
      const c = parseFloat(s?.carat ?? s?.carats ?? s?.weight ?? s) || 0;
      return sum + c;
    }, 0);
  }
  if (typeof data?.sideStones === "object" && data?.sideStones !== null) {
    return parseFloat(data.sideStones.carat ?? data.sideStones.carats ?? data.sideStones.weight ?? 0) || 0;
  }
  if (typeof data?.sideStone === "object" && data?.sideStone !== null) {
    return parseFloat(data.sideStone.carat ?? data.sideStone.carats ?? data.sideStone.weight ?? 0) || 0;
  }
  const caratVal = 
      data?.sideStoneCarat ??
      data?.sideStoneCarats ??
      data?.sideStonesCarat ??
      data?.sideStonesCarats ??
      data?.sideCarat ??
      data?.sideCarats ??
      data?.side_stone_carat ??
      data?.side_stone_carats ??
      data?.sideStoneWeight ??
      data?.sideWeight ??
      (typeof data?.sideStone === "number" || (!isNaN(Number(data?.sideStone)) && String(data?.sideStone).trim() !== "") ? data?.sideStone : null) ??
      (typeof data?.sideStones === "number" || (!isNaN(Number(data?.sideStones)) && String(data?.sideStones).trim() !== "") ? data?.sideStones : null);

  if (caratVal !== null && caratVal !== undefined) {
    if (Array.isArray(caratVal)) {
      return caratVal.reduce((sum: number, c: any) => sum + (parseFloat(c) || 0), 0);
    }
    const parsed = parseFloat(caratVal);
    if (!isNaN(parsed)) return parsed;
  }

  if (data?.sideStoneWeightInGrams) {
    return parseFloat(data.sideStoneWeightInGrams) / 0.2;
  }
  
  return 0;
};

interface WizardStep3WeightProps {
  actualWeight: string;
  setActualWeight: (value: string) => void;
  aiData: any;
  setAiData: (val: any) => void;
  validationErrors?: string[];
  previewUrls?: any[];
}

const WizardStep3Weight: React.FC<WizardStep3WeightProps> = ({ 
  actualWeight, setActualWeight, aiData, setAiData, validationErrors = [], previewUrls = []
}) => {
  const itemTypeStr = (aiData?.itemType ?? aiData?.category ?? '').trim().toLowerCase();
  const isDiamond = itemTypeStr === 'diamond' || itemTypeStr === 'diamonds' || itemTypeStr.includes('diamond');
  const isMandatory = {
    actualWeight: true,
  };

  const centerStoneCarats = getCenterStoneCarats(aiData);
  const sideStoneCarats = getSideStoneCarats(aiData);
  const centerStoneWeightGrams = centerStoneCarats * 0.2;
  const sideStoneWeightGrams = sideStoneCarats * 0.2;

  const aiGrossWeight = parseFloat(aiData?.grossWeightInGrams ?? aiData?.estimatedWeightInGrams ?? aiData?.grossWeight ?? 0);
  const aiNetWeight = aiData?.netWeightInGrams !== undefined && aiData?.netWeightInGrams !== null ? parseFloat(aiData.netWeightInGrams) : null;

  const grossVal = parseFloat(actualWeight);
  const isUnchanged = !isNaN(grossVal) && !isNaN(aiGrossWeight) && grossVal === aiGrossWeight;

  const netVal = isUnchanged && aiNetWeight !== null
    ? aiNetWeight
    : (!isNaN(grossVal) && actualWeight.trim() !== ''
      ? Math.max(0, Math.round((grossVal - centerStoneWeightGrams - sideStoneWeightGrams) * 1000) / 1000)
      : null);

  const formatWeight = (num: number | null): string => {
    if (num == null || isNaN(num)) return '';
    const str = num.toString();
    const decimalPart = str.split('.')[1] || '';
    if (decimalPart.length > 2) {
      return num.toFixed(decimalPart.length);
    }
    return num.toFixed(2);
  };

  const netWeightDisplay = formatWeight(netVal);

  useEffect(() => {
    const gross = parseFloat(actualWeight);
    if (!isNaN(gross) && actualWeight.trim() !== '') {
      const isUnchangedEffect = !isNaN(gross) && !isNaN(aiGrossWeight) && gross === aiGrossWeight;
      const calculatedNet = isUnchangedEffect && aiNetWeight !== null
        ? aiNetWeight
        : Math.max(0, Math.round((gross - centerStoneWeightGrams - sideStoneWeightGrams) * 1000) / 1000);
        
      const roundedNet = calculatedNet.toString();
      setAiData((prev: any) => {
        if (prev?.metalWeight !== roundedNet || prev?.netWeight !== roundedNet) {
          return {
            ...prev,
            grossWeight: actualWeight,
            metalWeight: roundedNet,
            netWeight: roundedNet,
          };
        }
        return prev;
      });
    } else {
      setAiData((prev: any) => {
        if (prev?.metalWeight || prev?.netWeight) {
          return {
            ...prev,
            metalWeight: '',
            netWeight: '',
          };
        }
        return prev;
      });
    }
  }, [actualWeight, centerStoneWeightGrams, sideStoneWeightGrams, aiGrossWeight, aiNetWeight]);

  return (
    <Grid container spacing={4}>

      <Grid item xs={12}>
        <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontFamily: '"Playfair Display", serif', fontSize: '1.75rem', color: '#1A1A1A' }}>Total weight</Typography>
        <Typography style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          Weigh the whole piece on any gram scale, stones included. Net weight (metal only) is calculated automatically.
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666' }}>GROSS WEIGHT <span style={{ color: '#d92b3c' }}>*</span></span>
          <span style={isDiamond
            ? { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#E11D48', background: '#FFF1F2', padding: '0.15rem 0.5rem', border: '1px solid #FDA4AF', borderRadius: '12px' }
            : { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#10B981', background: 'transparent', padding: '0.15rem 0.5rem', border: '1px solid #10B981', borderRadius: '12px' }
          }>{isDiamond ? 'CALCULATED' : 'YOU ENTER'}</span>
        </div>
        <CustomTextField
          variant="outlined"
          fullWidth
          size="small"
          type="text"
          disabled={isDiamond}
          value={actualWeight || ''}
          onChange={(e) => {
            let val = e.target.value;
            if (val === '.') val = '0.';
            else if (val.startsWith('.')) val = '0' + val;
            setActualWeight(val);
            const gross = parseFloat(val);
            const isUnchangedOnType = !isNaN(gross) && !isNaN(aiGrossWeight) && gross === aiGrossWeight;
            const calculatedNet = isUnchangedOnType && aiNetWeight !== null
              ? aiNetWeight
              : (!isNaN(gross) && val.trim() !== ''
                ? Math.max(0, Math.round((gross - centerStoneWeightGrams - sideStoneWeightGrams) * 1000) / 1000)
                : null);
            const netStr = calculatedNet != null ? calculatedNet.toString() : '';
            setAiData((prev: any) => ({
              ...prev,
              grossWeight: val,
              metalWeight: netStr,
              netWeight: netStr,
            }));
          }}
          name="actualWeight"
          style={{ background: isDiamond ? '#f9f9f9' : '#fff' }}
          InputProps={{ endAdornment: <span style={{ color: '#1A1A1A', fontWeight: 'bold', fontSize: '0.85rem' }}>g</span> }}
        />
        {validationErrors.includes('Gross Weight') && (
          <span style={{ color: '#d92b3c', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block', fontWeight: 500 }}>
            {isDiamond ? "* Gross Weight is mandatory and must be greater than 0." : "* Gross Weight is mandatory and must be at least 1g."}
          </span>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1px', color: '#666' }}>NET WEIGHT</span>
          <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#E11D48', background: '#FFF1F2', padding: '0.15rem 0.5rem', border: '1px solid #FDA4AF', borderRadius: '12px' }}>CALCULATED</span>
        </div>
        <CustomTextField 
          variant="outlined" 
          fullWidth 
          size="small" 
          type="text" 
          disabled
          value={netWeightDisplay} 
          style={{ background: '#f9f9f9' }} 
          InputProps={{ endAdornment: <span style={{ color: '#888', fontWeight: 'bold', fontSize: '0.85rem' }}>g</span> }}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={{ background: '#FFF0F2', border: '1px solid #FCEBEA', padding: '1rem', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '0.75rem', color: '#1A1A1A', fontSize: '0.85rem', marginTop: '1rem' }}>
          <span style={{ fontSize: '1.2rem', lineHeight: 1, color: '#d92b3c' }}>✦</span>
          <span>
            No scale handy? Skip it — our gemologist weighs the piece on receipt and the firm offer reflects the exact figure.
          </span>
        </div>
      </Grid>
    </Grid>
  );
};

export default WizardStep3Weight;
